/**
 * PerfHud — real-time performance metrics overlay for diagnosing scene performance on any device.
 *
 * Toggled with F3 (or Ctrl+Shift+M), forced on with ?perfhud=1, or via viewer.togglePerfHud().
 * Enabled by default; creators can disable it per scene with uiOptions.perfHudEnabled = false.
 *
 * Design constraints:
 * - ZERO per-frame work while hidden: the frame-time sampler subscribes to the app's frameupdate
 *   event only while visible and unsubscribes on hide.
 * - The heavier snapshot (splat counts, VRAM, collections) is assembled by a provider closure in
 *   createViewer at 4 Hz, not per frame.
 * - Everything is defensive: engine internals (unified gsplat manager, profiler stats) are read
 *   through optional chains inside try/catch — a missing field renders as 'n/a', never throws.
 * - "Copy diagnostics" puts the full snapshot JSON on the clipboard so it can be pasted into a
 *   bug report or straight to an assistant for analysis.
 */
import * as pc from 'playcanvas';

/** One HUD section: ordered key→value rows. Values are pre-formatted. */
export type PerfSnapshot = Record<string, Record<string, string | number | boolean | null>>;

const HUD_UPDATE_MS = 250;      // 4 Hz text refresh
const FRAME_RING = 120;         // frame-time window for min/avg/max

export class PerfHud {
    private readonly app: pc.Application;
    private readonly host: HTMLElement;
    private readonly provider: () => PerfSnapshot;
    private root: HTMLDivElement | null = null;
    private body: HTMLDivElement | null = null;
    private copyBtn: HTMLButtonElement | null = null;
    private visible = false;
    private timer: ReturnType<typeof setInterval> | null = null;
    // Frame-time ring buffer (ms), populated from frameupdate while visible.
    private readonly frameMs = new Float32Array(FRAME_RING);
    private frameIdx = 0;
    private frameCount = 0;
    private lastFrameAt = 0;
    private readonly onFrame = () => {
        const now = performance.now();
        if (this.lastFrameAt > 0) {
            this.frameMs[this.frameIdx] = now - this.lastFrameAt;
            this.frameIdx = (this.frameIdx + 1) % FRAME_RING;
            if (this.frameCount < FRAME_RING) this.frameCount++;
        }
        this.lastFrameAt = now;
    };

    constructor(app: pc.Application, host: HTMLElement, provider: () => PerfSnapshot) {
        this.app = app;
        this.host = host;
        this.provider = provider;
    }

    get isVisible(): boolean {
        return this.visible;
    }

    toggle(visible?: boolean): void {
        const want = visible ?? !this.visible;
        if (want === this.visible) return;
        if (want) this.show(); else this.hide();
    }

    private show(): void {
        this.visible = true;
        if (!this.root) this.buildDom();
        this.root!.style.display = 'block';
        this.lastFrameAt = 0;
        this.frameCount = 0;
        this.frameIdx = 0;
        this.app.on('frameupdate', this.onFrame);
        this.render();
        this.timer = setInterval(() => this.render(), HUD_UPDATE_MS);
    }

    private hide(): void {
        this.visible = false;
        this.app.off('frameupdate', this.onFrame);
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
        if (this.root) this.root.style.display = 'none';
    }

    destroy(): void {
        this.hide();
        this.root?.remove();
        this.root = null;
        this.body = null;
        this.copyBtn = null;
    }

    /** Frame stats from the HUD's own ring buffer (release engine builds don't profile these). */
    private frameStats(): Record<string, string | number> {
        if (this.frameCount === 0) return { 'fps (engine)': this.app.stats?.frame?.fps ?? 0 };
        let min = Infinity, max = 0, sum = 0;
        for (let i = 0; i < this.frameCount; i++) {
            const v = this.frameMs[i];
            if (v < min) min = v;
            if (v > max) max = v;
            sum += v;
        }
        const avg = sum / this.frameCount;
        return {
            'fps (engine)': this.app.stats?.frame?.fps ?? 0,
            'fps (avg window)': avg > 0 ? +(1000 / avg).toFixed(1) : 0,
            'frame ms avg': +avg.toFixed(2),
            'frame ms min/max': `${min.toFixed(2)} / ${max.toFixed(2)}`,
            '1% slowest ms': +this.slowest1pc().toFixed(2),
        };
    }

    private slowest1pc(): number {
        // Mean of the slowest ~1% (at 120 samples: the single worst frame; cheap + stable enough).
        const n = this.frameCount;
        if (n === 0) return 0;
        let worst = 0;
        for (let i = 0; i < n; i++) if (this.frameMs[i] > worst) worst = this.frameMs[i];
        return worst;
    }

    private snapshot(): PerfSnapshot {
        let snap: PerfSnapshot = {};
        try {
            snap = this.provider();
        } catch (e) {
            snap = { error: { provider: String(e) } };
        }
        // Frame section always comes from the HUD itself (first section).
        return { frame: this.frameStats(), ...snap };
    }

    private render(): void {
        if (!this.body) return;
        const snap = this.snapshot();
        let html = '';
        for (const [section, rows] of Object.entries(snap)) {
            html += `<div style="margin:6px 0 2px;color:#7fd4ff;font-weight:600;text-transform:uppercase;letter-spacing:.06em;font-size:10px">${escapeHtml(section)}</div>`;
            for (const [k, v] of Object.entries(rows)) {
                html += `<div style="display:flex;justify-content:space-between;gap:12px;line-height:1.45">`
                    + `<span style="color:#9aa4ae">${escapeHtml(k)}</span>`
                    + `<span style="color:#e8edf2;text-align:right">${escapeHtml(fmt(v))}</span></div>`;
            }
        }
        this.body.innerHTML = html;
    }

    private buildDom(): void {
        const root = document.createElement('div');
        root.className = 'storysplat-perf-hud';
        root.style.cssText = [
            'position:absolute', 'top:10px', 'right:10px', 'z-index:100050',
            'background:rgba(10,14,18,0.88)', 'color:#e8edf2',
            'font:11px/1.4 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
            'border:1px solid rgba(255,255,255,0.14)', 'border-radius:8px',
            'padding:8px 10px 10px', 'min-width:280px', 'max-width:360px',
            'max-height:calc(100% - 20px)', 'overflow-y:auto',
            'user-select:text', '-webkit-user-select:text', 'pointer-events:auto',
            'box-shadow:0 4px 18px rgba(0,0,0,0.45)',
        ].join(';');
        // Keep camera/gesture handlers from hijacking interactions with the panel.
        for (const ev of ['pointerdown', 'pointerup', 'wheel', 'touchstart', 'dblclick']) {
            root.addEventListener(ev, (e) => e.stopPropagation());
        }

        const header = document.createElement('div');
        header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:2px';
        const title = document.createElement('span');
        title.textContent = 'Performance (F3)';
        title.style.cssText = 'font-weight:700;color:#fff';
        const btns = document.createElement('span');
        btns.style.cssText = 'display:flex;gap:6px';
        const copy = document.createElement('button');
        copy.textContent = 'Copy diagnostics';
        copy.style.cssText = btnCss();
        copy.addEventListener('click', () => this.copyDiagnostics());
        const close = document.createElement('button');
        close.textContent = '✕';
        close.title = 'Close (F3)';
        close.style.cssText = btnCss();
        close.addEventListener('click', () => this.toggle(false));
        btns.appendChild(copy);
        btns.appendChild(close);
        header.appendChild(title);
        header.appendChild(btns);

        const body = document.createElement('div');
        root.appendChild(header);
        root.appendChild(body);
        this.host.appendChild(root);
        this.root = root;
        this.body = body;
        this.copyBtn = copy;
    }

    private copyDiagnostics(): void {
        const payload = {
            timestamp: new Date().toISOString(),
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
            ...this.snapshot(),
        };
        const text = JSON.stringify(payload, null, 2);
        const done = (ok: boolean) => {
            if (!this.copyBtn) return;
            const prev = 'Copy diagnostics';
            this.copyBtn.textContent = ok ? 'Copied ✓' : 'Copy failed';
            setTimeout(() => { if (this.copyBtn) this.copyBtn.textContent = prev; }, 1500);
        };
        try {
            navigator.clipboard.writeText(text).then(() => done(true), () => done(false));
        } catch {
            done(false);
        }
    }
}

function fmt(v: string | number | boolean | null): string {
    if (v === null || v === undefined) return 'n/a';
    if (typeof v === 'number') {
        if (!Number.isFinite(v)) return 'n/a';
        return Math.abs(v) >= 10000 && Number.isInteger(v) ? v.toLocaleString('en-US') : String(v);
    }
    return String(v);
}

function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function btnCss(): string {
    return [
        'background:rgba(255,255,255,0.08)', 'color:#cfd8e0', 'border:1px solid rgba(255,255,255,0.16)',
        'border-radius:5px', 'font:10px ui-monospace,Menlo,Consolas,monospace', 'padding:2px 7px', 'cursor:pointer',
    ].join(';');
}

/** Bytes → human string. */
export function fmtBytes(b: number | undefined | null): string {
    if (b === undefined || b === null || !Number.isFinite(b)) return 'n/a';
    if (b >= 1024 * 1024 * 1024) return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    if (b >= 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${b} B`;
}
